import { type ReactElement } from "react";
import { supabase } from "@baseline/web/supabase";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { MarkdownRenderer } from "../components/MarkdownRenderer";
import { useDocumentTitle } from "../hooks/useDocumentTitle";

export const Route = createFileRoute("/s/$shareToken")({
	component: SharedItemPage,
});

interface SharedData {
	title: string;
	markdown: string;
	sourceUrl: string | null;
}

function SharedItemPage(): ReactElement {
	const { shareToken } = Route.useParams();

	const query = useQuery({
		queryKey: ["shared", shareToken],
		queryFn: async (): Promise<SharedData | null> => {
			const { data: libraryItem, error } = await supabase
				.from("library_items")
				.select(
					`
					content_id,
					source_type,
					title,
					content,
					contents (title, canonical_url, markdown, notes)
				`,
				)
				.eq("share_token", shareToken)
				.eq("is_archived", false)
				.maybeSingle();

			if (error) throw error;
			if (!libraryItem) return null;

			type LibraryItemRow = {
				content_id: string | null;
				source_type: string;
				title: string | null;
				content: string | null;
				contents: {
					title: string | null;
					canonical_url: string;
					markdown: string | null;
					notes: string | null;
				} | null;
			};
			const item = libraryItem as unknown as LibraryItemRow;

			if (item.source_type === "document" && item.title) {
				return {
					title: item.title,
					markdown: item.content ?? "",
					sourceUrl: null,
				};
			}

			if (item.contents) {
				return {
					title: item.contents.title ?? item.contents.canonical_url,
					markdown:
						item.contents.notes || item.contents.markdown || "",
					sourceUrl: item.contents.canonical_url,
				};
			}

			return null;
		},
	});

	const data = query.data;

	useDocumentTitle(data?.title);

	if (query.isPending) {
		return (
			<div className="bg-surface text-fg flex min-h-screen flex-col font-sans antialiased">
				<div className="mx-auto max-w-[960px] px-6 py-10" />
			</div>
		);
	}

	if (!data) {
		return (
			<div className="bg-surface text-fg flex min-h-screen flex-col font-sans antialiased">
				<div className="mx-auto max-w-[960px] px-6 py-20">
					<div className="border-line-subtle bg-surface-raised rounded-lg border p-8 text-center">
						<h1 className="text-fg-strong text-xl font-semibold">
							This link is no longer available
						</h1>
						<p className="text-fg-faint mt-2">
							The owner may have stopped sharing this content.
						</p>
						<Link
							to="/"
							search={{
								mode: undefined,
								tag: undefined,
								type: undefined,
								summary: undefined,
							}}
							className="text-accent hover:text-fg-strong mt-4 inline-block transition-colors"
						>
							Go to clearvoid
						</Link>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="bg-surface text-fg flex min-h-screen flex-col font-sans antialiased">
			<nav className="border-line bg-nav-backdrop fixed top-0 z-50 w-full border-b backdrop-blur-xl">
				<div className="mx-auto flex h-20 max-w-[960px] items-center justify-between px-6">
					<Link
						to="/"
						search={{
							mode: undefined,
							tag: undefined,
							type: undefined,
							summary: undefined,
						}}
						className="text-2xl font-semibold tracking-tight"
					>
						clearvoid
					</Link>
				</div>
			</nav>
			<main className="flex-1 pt-20">
				<div className="pb-24">
					<section className="mx-auto max-w-[960px] px-6 pt-8">
						<section className="mb-6">
							<h1 className="text-fg-strong text-xl leading-snug font-semibold">
								{data.title}
							</h1>
							{data.sourceUrl && (
								<div className="text-fg-tertiary mt-1 text-sm">
									<a
										href={data.sourceUrl}
										target="_blank"
										rel="noopener noreferrer"
										className="hover:text-accent transition-colors"
									>
										{new URL(
											data.sourceUrl,
										).hostname.replace("www.", "")}
									</a>
								</div>
							)}
						</section>
					</section>

					<div className="mx-auto max-w-[960px] px-6">
						{data.markdown ? (
							<div data-testid="markdown-content">
								<MarkdownRenderer markdown={data.markdown} />
							</div>
						) : (
							<div className="border-line-subtle bg-surface-raised rounded-lg border p-8 text-center">
								<p className="text-fg-faint">
									No content available.
								</p>
							</div>
						)}
					</div>
				</div>
			</main>
		</div>
	);
}
